
import { AddUser, AddUsersToOrg, DisableUser, EnableUser, ListOrg, ListUser, UpdateUserPassword } from '@/api/superAdmin'
import { IUser, RecordStatus, IOrg } from '@/api/types'
import Pagination from '@/components/Pagination/index.vue'
import { formatDate, generateRandomPassword } from '@/utils/index'
import { Form as ElForm } from 'element-ui'
import { MessageBoxData, MessageBoxInputData } from 'element-ui/types/message-box'
import { Component, Vue } from 'vue-property-decorator'

@Component({
  name: 'SuperAdminUsers',
  components: {
    Pagination
  }
})
export default class extends Vue {
  public list: IUser[] = []
  public total = 0
  public listLoading = true
  public orgList: IOrg[] = []
  public orgOptions: IOrg[] = []
  public orgLoading = false
  public orgDialogList: IOrg[] = []
  public orgDialogLoading = false
  public inputKeyword = ''
  // 新增批量操作相关状态
  public isBatchOperating = false
  public selectedRows: IUser[] = []

  public listQuery = {
    pageNum: 1,
    pageSize: 50,
    keywords: [] as string[],
    status: undefined,
    orgIds: [] as number[],
    queryScope: 0
  }

  public statusOptions = [
    { label: '全部', value: undefined },
    { label: '启用', value: [RecordStatus.Enabled] },
    { label: '禁用', value: [RecordStatus.Disabled] }
  ]

  public adminOptions = [
    { label: '全部', value: 0},
    { label: '管理员', value: 1 },
    { label: '普通用户', value: 2 }
  ]

  // 创建用户相关
  public createDialogVisible = false
  public createForm = {
    username: '',
    phone: '',
    password: ''
  }

  public createRules = {
    username: [
      { required: true, message: '请输入用户名', trigger: 'blur' },
      { min: 3, max: 20, message: '长度在 3 到 20 个字符', trigger: 'blur' }
    ],
    password: [
      { required: true, message: '请输入初始密码', trigger: 'blur' },
      { min: 6, max: 20, message: '长度在 6 到 20 个字符', trigger: 'blur' }
    ]
  }

  // 组织相关
  public orgDialogVisible = false
  public orgForm = {
    userIds: [] as number[],
    orgId: null as number | null,
  }

  public orgRules = {
    orgId: [
      { required: true, message: '请选择组织', trigger: 'change' }
    ]
  }

  created() {
    this.getList()
    this.fetchOrgs()
  }

  public async getList() {
    this.listLoading = true
    try {
      const { data } = await ListUser(this.listQuery)
      this.list = data.items
      this.total = data.total
    } catch (error) {
      console.error('获取用户列表失败:', error)
      this.$message.error('获取用户列表失败')
    } finally {
      this.listLoading = false
    }
  }

  public async fetchOrgs() {
    try {
      // 这里需要调用获取组织列表的API
      const { data } = await ListOrg()
      this.orgList = data.items
      this.orgOptions = [...this.orgList]
    } catch (error) {
      console.error('获取组织列表失败:', error)
    }
  }

  public async remoteSearchOrg(query: string) {
    if (query) {
      this.orgLoading = true
      try {
        const { data } = await ListOrg()
        this.orgOptions = data.items
      } catch (error) {
        console.error('搜索组织失败:', error)
      } finally {
        this.orgLoading = false
      }
    } else {
      this.orgOptions = [...this.orgList]
    }
  }

  public async remoteSearchOrgForDialog(query: string) {
    if (query) {
      this.orgDialogLoading = true
      try {
        const { data } = await ListOrg()
        this.orgDialogList = data.items
      } catch (error) {
        console.error('搜索组织失败:', error)
      } finally {
        this.orgDialogLoading = false
      }
    } else {
      this.orgDialogList = [...this.orgList]
    }
  }

  public handleFilter() {
    this.listQuery.pageNum = 1
    this.getList()
  }

  public formatTime(time: string) {
    if (!time) return ''
    return formatDate(time, 'YYYY-MM-DD HH:mm:ss')
  }

  public handleCreate() {
    const randomPassword = generateRandomPassword(10)
    this.createForm = {
      username: '',
      phone: '',
      password: randomPassword
    }
    this.createDialogVisible = true
    this.$nextTick(() => {
      (this.$refs.createForm as ElForm).clearValidate()
    })
  }

  public async createData() {
    try {
      await AddUser({
        username: this.createForm.username,
        password: this.createForm.password,
        phone: this.createForm.phone
      })
      this.$message.success('用户创建成功')
      this.createDialogVisible = false
      this.getList()
    } catch (error) {
      console.error('创建用户失败:', error)
    }
  }

  public handleEnable(row: IUser) {
    this.$confirm('确定要启用该用户吗?', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async() => {
      await EnableUser({ id: row.id })
      this.$message.success('用户已启用')
      this.getList()
    })
  }

  public handleDisable(row: IUser) {
    this.$confirm('确定要禁用该用户吗?', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async() => {
      await DisableUser({ id: row.id })
      this.$message.success('用户已禁用')
      this.getList()
    })
  }

  public handleUpdatePassword(row: IUser) {
    const randomPassword = generateRandomPassword(10)
    this.$prompt('请输入新密码', '修改密码', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputType: 'password',
      inputValue: randomPassword,
      inputPattern: /^.{6,20}$/,
      inputErrorMessage: '密码长度必须在6-20个字符之间'
    }).then(async(value: MessageBoxData) => {
      const password = (value as MessageBoxInputData).value
      await UpdateUserPassword({ id: row.id, password: password})
      this.$message.success('密码修改成功')
    })
  }

  // 切换批量操作模式
  public toggleBatchOperation() {
    this.isBatchOperating = !this.isBatchOperating
    if (!this.isBatchOperating) {
      this.selectedRows = []
    }
  }

  // 处理表格选择变化
  public handleSelectionChange(rows: IUser[]) {
    this.selectedRows = rows
  }

  // 处理批量添加到组织
  public handleBatchAddToOrg() {
    if (this.selectedRows.length === 0) {
      this.$message.warning('请先选择用户')
      return
    }

    this.orgForm.userIds = this.selectedRows.map(row => row.id)
    this.orgForm.orgId = null
    this.orgDialogList = [...this.orgList]
    this.orgDialogVisible = true
  }

  public handleAddToOrg(row: IUser) {
    this.orgForm.userIds = [row.id]
    this.orgForm.orgId = null
    this.orgDialogList = [...this.orgList]
    this.orgDialogVisible = true
  }

  public async addToOrg() {
    try {
      // 确保orgId是数字类型
      if (this.orgForm.orgId !== null) {
        await AddUsersToOrg({
          userIds: this.orgForm.userIds,
          orgId: Number(this.orgForm.orgId)
        })
        this.$message.success('添加至组织成功')
        this.orgDialogVisible = false
        this.getList()
      }
    } catch (error) {
      console.error('添加至组织失败:', error)
    }
  }

  public copyPassword(password: string) {
    const textarea = document.createElement('textarea')
    textarea.value = password
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    this.$message.success('密码已复制到剪贴板')
  }

  public regeneratePassword() {
    this.createForm.password = generateRandomPassword(10)
    this.$message.success('已重新生成密码')
  }

  public handleAddKeyword() {
    const keyword = this.inputKeyword.trim()
    if (keyword && !this.listQuery.keywords.includes(keyword)) {
      this.listQuery.keywords.push(keyword)
      this.inputKeyword = ''
    }
  }

  public handleRemoveKeyword(index: number) {
    this.listQuery.keywords.splice(index, 1)
  }
}
